import React from "react";
import * as Sentry from "@sentry/react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { produce } from "immer";
import { useAppStore } from "./appStore";
import DocumentModel from "../models/Document";
import _ from "lodash";
import scrollIntoViewIfNeeded from "scroll-into-view-if-needed";
import Subscription from "../models/Subscription";
import { useSudowriteStore } from "./sudowriteStore";
import POVTense from "../models/POVTense";
import Document from "../models/Document";
import { TENSES, POVS, isAutoPOV } from "../../../src/shared/constants";
import { getDefaultIsMultiplePOVState, normalizeIsMultiplePOVState } from "../../../src/shared/utils/isMultiplePOVState";
import validateCharactersArray from "../../../src/shared/utils/validateCharactersArray";
import { createSafeStorage } from "./safeStorage";
import { clampCommentSidebarWidth, COMMENT_SIDEBAR_MIN_WIDTH } from "../components/TiptapDocumentSB/commentSidebarWidth";

/** Scroll events before we treat the user as having scrolled away from auto-scroll (not reactive). */
let autoScrollEventCount = 0;

// Separate from standalone saves; series writes may outlive the mounted Outline.
const pendingSeriesPOVSaves = new Map();

/** Collapsed side-bar rail width: 48px circle re-open button + 10px padding on both sides. */
export const COLLAPSED_BAR_WIDTH = 68;

const initialState = {
  project: null,
  contents: null,
  document: null,
  deletedDocuments: null,
  editingTitle: null,
  isEditing: null,
  isEditingDocumentTitle: false,
  isEditingTextarea: false,
  creatingDocument: false,
  innerRef: { current: null },
  leftRef: { current: null },
  menuRef: { current: null },
  editorSectionRef: { current: null },
  columnWidths: {
    left: { current: 300, min: 150, initial: 300, collapsed: false, collapsedMin: COLLAPSED_BAR_WIDTH, openMin: 150, saved: 300 },
    center: { current: null, min: 508, initial: 430, collapsed: false },
    right: { current: 350, min: 350, initial: 350, collapsed: false, collapsedMin: COLLAPSED_BAR_WIDTH, openMin: 350, saved: 350 },
  },
  /** Live editor resize hooks registered by TiptapDocumentSB for project-level gutters. */
  columnLayoutResizeHooks: {
    resizeCenterColumn: () => {},
    editorContainer: null,
  },
  resizingColumns: false,
  userScrolled: false,
  feedbackOpen: false,
  feedbackInvocation: "",
  trashedItem: null,
  /** Set by LeftBar when mounted; called after restoring a trashed chat from the center column so trash UI closes. */
  exitTrashView: () => {},
  refreshTrashedItems: () => {},
  isDeletingDocumentFolder: false,
  showNewDocumentCTAs: false,
  latestDocumentRequest: null, // track the latest active document request to prevent race conditions
  selectedDocumentId: null, // tracks the selected document ID for instant UI feedback (before document loads)
  /** Set when chat opens a comment in another document; EditorContainer clears after expanding. */
  pendingChatCommentNavigation: null,
  /** Set when chat jumps to a highlight in another document; TiptapDocumentSB clears after scrolling. */
  pendingManuscriptHighlightNavigation: null,
  pendingFrontendSpacingCleanup: null,
  spacingCleanupRetryJobId: null,
  locallyResolvedSpacingCleanupDocumentIds: [],
  showLinkingMenu: false,
  isCreatingNewBlankDocument: false, // flag for when we are creating a blank doc vs a linked chapter doc

  // POV/Tense
  povTense: {
    tense: {
      value: TENSES.PAST,
      userChanged: false,
    },
    pov: {
      value: POVS.THIRD_PERSON_LIMITED,
      characters: [],
      userChanged: false,
    },
  },
  isMultiplePOV: getDefaultIsMultiplePOVState(), // whether the project has multiple POVs enabled
  currentDocumentPOVTense: {
    tense: {
      value: TENSES.PAST,
      userChanged: false,
    },
    pov: {
      value: POVS.THIRD_PERSON_LIMITED,
      characters: [],
      userChanged: false,
    },
  },

  currentDocumentPOVTenseStatus: "ready", // ready | loading | error
  _documentPOVTenseLinkedChapter: null, // retain fallback context for a failed-read retry

  // Feature flags
  hasAccessToPOVAndTenseBeta: false,
  _povTenseSaving: false, // guard to prevent getPOVTense from overwriting optimistic updates during save
  _povTenseEpoch: 0, // monotonic counter — incremented on every save or new fetch; stale reads are discarded
  _setDocumentEpoch: 0, // monotonic counter for setDocument — only the latest call can write state

  /**
   * Returns the lib/Editor wrapper for the open manuscript (set by TiptapDocumentSB when the editor is ready).
   * Used for agent-driven highlight (e.g. apply_ui_action → highlight_manuscript).
   */
  manuscriptEditorGetter: null,

  // Title cleaning animations
  // Maps projectId -> { oldTitle, newTitle, isAnimating }
  titleAnimations: {},

  // SW-8335 — Dean's bug fix: persist the comment sidebar's "user has explicitly
  // closed it" state per-document so the sidebar does NOT auto-reopen on the
  // next document load.
  //
  // Shape: { [documentId: string]: true }. Only present keys count as "closed";
  // missing or non-true values mean "no prior close, follow default behavior".
  // The map is persisted via the partialize at the bottom of this file.
  userClosedCommentSidebarByDocId: {},

  // SW-10124 — persisted comment sidebar width (px). Minimum/default is 320.
  commentSidebarWidth: COMMENT_SIDEBAR_MIN_WIDTH,
  /** Live width while dragging; not persisted. */
  commentSidebarWidthPreview: null,
};

export const useProjectStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      resetSpacingCleanupRetryState: (jobId = null) => set({
        pendingFrontendSpacingCleanup: null,
        spacingCleanupRetryJobId: jobId,
        locallyResolvedSpacingCleanupDocumentIds: [],
      }),
      setPendingFrontendSpacingCleanup: (action) => set({ pendingFrontendSpacingCleanup: action }),
      clearPendingFrontendSpacingCleanup: (expected = null) => set((state) => {
        if (expected && state.pendingFrontendSpacingCleanup !== expected) return state;
        return { pendingFrontendSpacingCleanup: null };
      }),
      markSpacingCleanupDocumentLocallyResolved: ({ jobId, documentId }) => set((state) => {
        const existing = state.spacingCleanupRetryJobId === jobId
          ? state.locallyResolvedSpacingCleanupDocumentIds
          : [];
        if (existing.includes(documentId)) return state;
        return {
          spacingCleanupRetryJobId: jobId,
          locallyResolvedSpacingCleanupDocumentIds: [...existing, documentId],
        };
      }),

      setProject: async (project) => {
        if (_.isEqual(get().project, project)) {
          return;
        }

        set({ project });

        // Get the POV and Tense from the Postgres DB whenever the project is updated
        if (project) {
          const userId = get().project?.data.userId;
          if (!userId) {
            return null;
          }

          const { subscription, subscriptionJSON } = useSudowriteStore.getState();
          let hasAccessToPOVAndTenseBeta;
          if (typeof subscriptionJSON?.hasAccessToPOVAndTenseBeta === "boolean") {
            hasAccessToPOVAndTenseBeta = subscriptionJSON.hasAccessToPOVAndTenseBeta;
          } else if (typeof subscription?.hasAccessToPOVAndTenseBeta === "boolean") {
            hasAccessToPOVAndTenseBeta = subscription.hasAccessToPOVAndTenseBeta;
          } else {
            const fetchedSubscription = await Subscription.fetchFromUserId(userId);
            if (!fetchedSubscription) {
              return null;
            }
            hasAccessToPOVAndTenseBeta = fetchedSubscription.getFeatureFlags().hasAccessToPOVAndTenseBeta;
          }

          if (typeof hasAccessToPOVAndTenseBeta !== "undefined") {
            set({ hasAccessToPOVAndTenseBeta });
          }

          if (hasAccessToPOVAndTenseBeta) {
            get().getPOVTense();
          }
        }
      },

      setRightBarCollapsed: (collapsed) => {
        get().setColumnWidths((draft) => {
          draft.right.collapsed = collapsed;
          draft.right.min = collapsed ? draft.right.collapsedMin : draft.right.openMin;
          if (!draft.right.collapsed && draft.right.current < draft.right.min) {
            draft.right.current = draft.right.min;
          }
          if (collapsed) {
            draft.right.saved = draft.right.current;
            draft.right.current = draft.right.min;
          } else {
            draft.right.current = Math.max(draft.right.saved || draft.right.min, draft.right.current);
            // On narrow widths, prioritize the newly opened right bar by collapsing the opposite side first.
            if (window.innerWidth - draft.left.current - draft.right.current < draft.center.min && !draft.left.collapsed) {
              draft.left.collapsed = true;
              draft.left.min = draft.left.collapsedMin;
              draft.left.saved = draft.left.current;
              draft.left.current = draft.left.min;
            }
          }
          draft.center.current = window.innerWidth - draft.left.current - draft.right.current;
        });

        setTimeout(() => {
          // force a resize event to make sure the editor layout updates
          window.dispatchEvent(new Event("resize"));
          if (!collapsed) {
            void import("src/lib/editorSelectionContextSync").then(({ reconcileEditorSelectionContextSync }) => {
              reconcileEditorSelectionContextSync();
            });
          }
        }, 10);
      },

      setLeftBarCollapsed: (collapsed) => {
        get().setColumnWidths((draft) => {
          draft.left.collapsed = collapsed;
          draft.left.min = collapsed ? draft.left.collapsedMin : draft.left.openMin;
          if (!draft.left.collapsed && draft.left.current < draft.left.min) {
            draft.left.current = draft.left.min;
          }
          if (collapsed) {
            draft.left.saved = draft.left.current;
            draft.left.current = draft.left.min;
          } else {
            draft.left.current = Math.max(draft.left.saved || draft.left.min, draft.left.current);
            // On narrow widths, prioritize the newly opened left bar by collapsing the opposite side first.
            if (window.innerWidth - draft.left.current - draft.right.current < draft.center.min && !draft.right.collapsed) {
              draft.right.collapsed = true;
              draft.right.min = draft.right.collapsedMin;
              draft.right.saved = draft.right.current;
              draft.right.current = draft.right.min;
            }
          }
          draft.center.current = window.innerWidth - draft.left.current - draft.right.current;
        });

        setTimeout(() => {
          // force a resize event to make sure the editor layout updates
          window.dispatchEvent(new Event("resize"));
        }, 10);
      },

      setContents: (contents) => {
        const withFloats = contents.map((c) => ({ ...c, displayOrder: parseFloat(c.displayOrder || 0) }));
        const sorted = withFloats.sort((a, b) => a.displayOrder - b.displayOrder);
        set({ contents: sorted });
      },

      setDocument: async (document, linkedChapter = null) => {
        // Only the latest navigation/retry may install settings, including A→B→A.
        const requestEpoch = get()._setDocumentEpoch + 1;
        const needsRead = get().hasAccessToPOVAndTenseBeta && document
          && (document.povTenseId || linkedChapter?.povTenseId);
        set({
          document,
          _setDocumentEpoch: requestEpoch,
          _documentPOVTenseLinkedChapter: linkedChapter,
          currentDocumentPOVTenseStatus: needsRead ? "loading" : "ready",
          // Keep the shape expected by consumers, but never carry values from
          // the previous document into a pending or failed read.
          currentDocumentPOVTense: needsRead ? {
            pov: { value: null, characters: [], userChanged: false },
            tense: { value: null, userChanged: false },
          } : get().povTense,
        });
        if (!needsRead) return;

        try {
          let values = document.povTenseId ? await POVTense.findById(document.povTenseId) : null;
          if (get()._setDocumentEpoch !== requestEpoch) return;
          if (!values && linkedChapter?.povTenseId) {
            values = await POVTense.findById(linkedChapter.povTenseId);
            if (get()._setDocumentEpoch !== requestEpoch) return;
          }
          // A missing record falls back; a failed request stays unavailable.
          // These are distinct: a network failure cannot establish saved values.
          set({ currentDocumentPOVTense: values || get().povTense, currentDocumentPOVTenseStatus: "ready" });
        } catch (error) {
          if (get()._setDocumentEpoch !== requestEpoch) return;
          set({ currentDocumentPOVTenseStatus: "error" });
          Sentry.captureException(error, { tags: { feature: "set_document_POV_tense" } });
        }
      },
      retryDocumentPOVTense: () => get().setDocument(get().document, get()._documentPOVTenseLinkedChapter),

      setDeletedDocuments: (deletedDocuments) => set({ deletedDocuments }),

      setEditingTitle: (editingTitle) => set({ editingTitle }),

      setIsEditingDocumentTitle: (isEditingDocumentTitle) => set({ isEditingDocumentTitle }),

      setIsEditing: (isEditing) => set({ isEditing }),

      setIsEditingTextarea: (isEditingTextarea) => set({ isEditingTextarea }),

      setCreatingDocument: (creatingDocument) => set({ creatingDocument }),

      setColumnWidths: (columnWidths) =>
        typeof columnWidths === "function"
          ? set((state) => {
              return { columnWidths: produce(state.columnWidths, columnWidths) };
            })
          : set({ columnWidths }),

      setColumnLayoutResizeHooks: (columnLayoutResizeHooks) => set({ columnLayoutResizeHooks }),

      setResizingColumns: (resizingColumns) => set({ resizingColumns }),

      setUserScrolled: (userScrolled) => set({ userScrolled }),

      setDocumentId: async (id, forceEditorValue = null, scrollTo = null, linkedChapter = null) => {
        const document = get().document;
        const scrollEditorToTop = (smoothScroll) => {
          const editorSection = get().editorSectionRef?.current;
          if (!editorSection) return;

          const scrollContainer = editorSection.closest?.(".editor-stack") || editorSection.parentElement || editorSection;
          const scrollBehavior = smoothScroll ? "smooth" : "auto";
          const scroll = () => {
            if (typeof scrollContainer.scrollTo === "function") {
              scrollContainer.scrollTo({ top: 0, behavior: scrollBehavior });
            } else {
              scrollContainer.scrollTop = 0;
            }
          };

          const activeElement = window.document?.activeElement;
          if (activeElement && scrollContainer.contains(activeElement)) {
            activeElement.blur();
          }

          scroll();
          window.requestAnimationFrame(scroll);
          setTimeout(scroll, 200);
        };

        // Don't fetch if the document is already the ID we want
        if (id === document?.id) {
          if (scrollTo !== null) {
            scrollEditorToTop(Boolean(scrollTo));
          }
          return;
        }

        // Chat "open this comment in another doc" — if user picks a different doc in the tree, cancel that flow.
        const pendingNav = get().pendingChatCommentNavigation;
        if (pendingNav && pendingNav.documentId !== id) {
          set({ pendingChatCommentNavigation: null });
        }
        const pendingHighlight = get().pendingManuscriptHighlightNavigation;
        if (pendingHighlight && pendingHighlight.documentId !== id) {
          set({ pendingManuscriptHighlightNavigation: null });
        }

        const requestId = Date.now();
        // INSTANT: Update selectedDocumentId immediately for instant file tree feedback
        set({ trashedItem: null, latestDocumentRequest: requestId, selectedDocumentId: id });

        const newDocument = new DocumentModel({ id: id, userId: useAppStore.getState().user.uid });
        await newDocument.init();

        // is this still the latest request?
        if (get().latestDocumentRequest === requestId) {
          get().setDocument(newDocument, linkedChapter);
        }
      },

      setFeedbackOpen: (feedbackOpen) => set({ feedbackOpen }),

      setFeedbackInvocation: (feedbackInvocation) => set({ feedbackInvocation }),

      setPendingChatCommentNavigation: (pendingChatCommentNavigation) => set({ pendingChatCommentNavigation }),

      setPendingManuscriptHighlightNavigation: (pendingManuscriptHighlightNavigation) =>
        set({ pendingManuscriptHighlightNavigation }),

      /**
       * SW-8335 — Mark this document's comment sidebar as user-closed.
       * Persisted (via partialize) so subsequent reloads keep the sidebar shut
       * for this document, even when comments are present.
       */
      markCommentSidebarUserClosed: (documentId) => {
        if (!documentId) return;
        set((state) => {
          const current = state.userClosedCommentSidebarByDocId || {};
          if (current[documentId] === true) return {}; // idempotent — avoid unnecessary update
          return {
            userClosedCommentSidebarByDocId: { ...current, [documentId]: true },
          };
        });
      },

      /**
       * SW-8335 — Clear the user-closed flag for a document (the user just
       * explicitly reopened the sidebar on this document, so it should resume
       * the default auto-open behavior on later reloads).
       */
      clearCommentSidebarUserClosed: (documentId) => {
        if (!documentId) return;
        set((state) => {
          const current = state.userClosedCommentSidebarByDocId || {};
          if (!(documentId in current)) return {}; // nothing to remove
          const next = { ...current };
          delete next[documentId];
          return { userClosedCommentSidebarByDocId: next };
        });
      },

      /**
       * SW-10124 — Persist the user's preferred comment sidebar width.
       */
      setCommentSidebarWidth: (width) => {
        const clamped = clampCommentSidebarWidth(width);
        if (get().commentSidebarWidth === clamped) return;
        set({ commentSidebarWidth: clamped, commentSidebarWidthPreview: null });
      },

      setCommentSidebarWidthPreview: (width) => {
        const clamped = width == null ? null : clampCommentSidebarWidth(width);
        if (get().commentSidebarWidthPreview === clamped) return;
        set({ commentSidebarWidthPreview: clamped });
      },

      reset: () => {
        autoScrollEventCount = 0;
        set(initialState);
      },

      automaticScroll: (ref) => {
        // only scroll if the user hasn't scrolled or isn't editing something else
        if (get().userScrolled) {
          return;
        } else if (get().isEditingTextarea) {
          set({ userScrolled: true });
          return;
        }

        if (ref?.current) {
          scrollIntoViewIfNeeded(ref.current, { behavior: "smooth", scrollMode: "if-needed", block: "end" });
        }
      },

      resetScrollCount: () => {
        autoScrollEventCount = 0;
      },

      incrementScrollCount: () => {
        autoScrollEventCount += 1;
      },

      getScrollCount: () => autoScrollEventCount,

      setTrashedItem: (trashedItem) => set({ trashedItem }),
      setExitTrashView: (exitTrashView) => set({ exitTrashView: typeof exitTrashView === "function" ? exitTrashView : () => {} }),
      setRefreshTrashedItems: (refreshTrashedItems) => set({ refreshTrashedItems }),
      setIsDeletingDocumentFolder: (isDeletingDocumentFolder) => set({ isDeletingDocumentFolder }),
      setShowNewDocumentCTAs: (showNewDocumentCTAs) => {
        if (get().showNewDocumentCTAs === showNewDocumentCTAs) return;
        set({ showNewDocumentCTAs });
      },
      setShowLinkingMenu: (showLinkingMenu) => {
        if (get().showLinkingMenu === showLinkingMenu) return;
        set({ showLinkingMenu });
      },
      setIsCreatingNewBlankDocument: (isCreatingNewBlankDocument) => set({ isCreatingNewBlankDocument }),
      setStorePOVTense: (povTense) => {
        if (!povTense) {
          return;
        }

        if (!get().hasAccessToPOVAndTenseBeta) {
          return;
        }

        set({ povTense });
      },
      applySeriesPOVTense: (projectId, povTense, isMultiplePOV) => {
        if (get().project?.id !== projectId) return;
        set((state) => ({
          // Omitted fields stay intact; explicit undefined still clears a failed edit.
          povTense: { ...state.povTense, ...povTense },
          isMultiplePOV: isMultiplePOV == null ? state.isMultiplePOV : normalizeIsMultiplePOVState(isMultiplePOV),
          _povTenseEpoch: state._povTenseEpoch + 1,
        }));
      },
      beginSeriesPOVTenseSave: (projectId) => {
        const token = {};
        const saves = pendingSeriesPOVSaves.get(projectId) || new Set();
        saves.add(token);
        pendingSeriesPOVSaves.set(projectId, saves);
        const invalidate = () => {
          if (get().project?.id === projectId) set((state) => ({ _povTenseEpoch: state._povTenseEpoch + 1 }));
        };
        invalidate();
        return () => {
          saves.delete(token);
          if (!saves.size) pendingSeriesPOVSaves.delete(projectId);
          invalidate();
        };
      },
      getPOVTense: async () => {
        if (!get().hasAccessToPOVAndTenseBeta) {
          return;
        }

        // Skip if a POV save is in flight — the optimistic update is more current than Prisma
        if (get()._povTenseSaving || pendingSeriesPOVSaves.has(get().project?.id)) {
          return;
        }

        const project = get().project;
        if (!project) {
          return;
        }

        // Capture a monotonic epoch so we can detect if a newer fetch or save
        // started while this one was in flight.
        const epoch = get()._povTenseEpoch + 1;
        set({ _povTenseEpoch: epoch });

        const response = await project.getPOVTense();

        // Re-check after await — a save may have started while we were fetching,
        // or a newer getPOVTense call may have incremented the epoch past ours.
        if (get().project?.id !== project.id || get()._povTenseSaving || pendingSeriesPOVSaves.has(project.id) || get()._povTenseEpoch !== epoch) {
          return;
        }

        if (!response || !response.povTense) {
          // Let's initialize the database value with the default POV and Tense
          // If the project was created within the last 2 minutes, it's a new project and we should use the default POV and Tense (THIRD_PERSON_LIMITED and PAST)
          // Otherwise, we should use the AUTO POV and Tense so that we don't interfere with the user's existing response style
          const isNewProject = project.isNew();
          const pov = isNewProject ? POVS.THIRD_PERSON_LIMITED : POVS.AUTO;
          const tense = isNewProject ? TENSES.PAST : TENSES.AUTO;

          const newPOVTense = {
            pov: { value: pov, characters: [], userChanged: false },
            tense: { value: tense, userChanged: false },
          };

          // Set the store optimistically before the DB write. If a user
          // save fires during the await, their setPOVTense will override
          // these defaults in both store and DB.
          set({
            povTense: newPOVTense,
            currentDocumentPOVTense: newPOVTense,
            isMultiplePOV: getDefaultIsMultiplePOVState(),
          });

          try {
            await project.setPOVTense(newPOVTense);
          } catch (err) {
            Sentry.captureException(err, { tags: { feature: "initPOVTenseDefaults" } });
          }
          return;
        }

        const normalizedMultiplePOV = normalizeIsMultiplePOVState(response.isMultiplePOV);
        if (response.povTense) {
          set((state) => {
            const shouldUpdateCurrentDocument =
              !normalizedMultiplePOV.value || (!state.document?.povTenseId && !state.currentDocumentPOVTense?.pov?.value);

            return {
              povTense: response.povTense,
              ...(shouldUpdateCurrentDocument ? { currentDocumentPOVTense: response.povTense } : {}),
              isMultiplePOV: normalizedMultiplePOV,
            };
          });
        } else {
          set({ isMultiplePOV: normalizedMultiplePOV });
        }
      },
      setPOVTense: async ({ pov, tense }) => {
        if (!pov && !tense) {
          return;
        }

        if (!get().hasAccessToPOVAndTenseBeta) {
          return;
        }

        const project = get().project;
        if (!project) {
          return;
        }

        const currentPOVTense = get().povTense;

        // Avoid parameter mutation by creating a copy
        const povCopy = pov ? { ...pov } : null;
        let characters = povCopy?.characters || currentPOVTense.pov.characters;

        // Validate and clean characters array
        if (characters && Array.isArray(characters)) {
          characters = validateCharactersArray(characters);
        } else {
          characters = [];
        }

        if (povCopy?.value === POVS.THIRD_PERSON_OMNISCIENT || isAutoPOV(povCopy?.value)) {
          characters = [];
        }

        // Simplified structure without unnecessary wrapper
        const newPOVTense = {
          pov: {
            value: povCopy?.value?.toLowerCase() || currentPOVTense.pov.value,
            characters,
            userChanged: povCopy?.userChanged || currentPOVTense.pov.userChanged,
          },
          tense: {
            value: tense?.value?.toLowerCase() || currentPOVTense.tense.value,
            userChanged: tense?.userChanged || currentPOVTense.tense.userChanged,
          },
        };

        // Optimistically update the POV and Tense in the store and bump the
        // epoch so any in-flight getPOVTense fetch will be discarded.
        set((state) => ({
          _povTenseSaving: true,
          _povTenseEpoch: state._povTenseEpoch + 1,
          povTense: {
            ...state.povTense,
            ...newPOVTense,
          },
        }));

        try {
          // Update the POV and Tense in the Postgres DB
          const result = await project.setPOVTense(newPOVTense);
          set({ _povTenseSaving: false });
          return result;
        } catch (error) {
          Sentry.captureException(error, { tags: { feature: "setPOVTenseForProject" } });

          // Rollback using current state instead of stale captured state
          set({ _povTenseSaving: false });
          return set((state) => ({
            povTense: {
              ...state.povTense,
              ...currentPOVTense,
            },
          }));
        }
      },
      createPOVTenseForDocument: async (project, doc, povTense) => {
        if (!get().hasAccessToPOVAndTenseBeta) {
          return;
        }

        const newPOVTense = await POVTense.create(povTense.pov, povTense.tense, project.id, project.data.userId, doc.id);
        if (!newPOVTense) {
          return;
        }

        // Add the new POV Tense ID to the document
        const document = new Document({ id: doc.id, userId: project.data.userId });
        await document.update({ povTenseId: newPOVTense.id });
        return newPOVTense.id;
      },
      setPOVTenseForDocument: async (povTense) => {
        if (!get().hasAccessToPOVAndTenseBeta) {
          return;
        }

        const currentDocumentPOVTense = get().currentDocumentPOVTense;

        set((state) => ({
          currentDocumentPOVTense: povTense,
          currentDocumentPOVTenseStatus: "ready",
          _povTenseSaving: true,
          _povTenseEpoch: state._povTenseEpoch + 1,
          // Also bump _setDocumentEpoch so any in-flight setDocument fetch
          // is discarded — the per-chapter save is more current.
          _setDocumentEpoch: state._setDocumentEpoch + 1,
        }));

        const project = get().project;
        if (!project) {
          return null;
        }

        const doc = get().document;
        if (!doc) {
          return null;
        }

        const documentEpoch = get()._setDocumentEpoch;
        const previousDocumentPovTenseId = doc.povTenseId || null;

        let povTenseId = doc.povTenseId;
        if (!povTenseId) {
          povTenseId = await get().createPOVTenseForDocument(project, doc, povTense);
        }

        if (povTenseId && previousDocumentPovTenseId !== povTenseId) {
          set((state) => ({
            document: state.document && state.document.id === doc.id ? { ...state.document, povTenseId } : state.document,
          }));
        }

        let characters = povTense.pov.characters;

        // Validate and clean characters array
        if (characters && Array.isArray(characters)) {
          characters = validateCharactersArray(characters);
        } else {
          characters = [];
        }

        if (povTense.pov.value === POVS.THIRD_PERSON_OMNISCIENT || isAutoPOV(povTense.pov.value)) {
          characters = [];
        }

        try {
          const povTenseResponse = await project.updateDocumentPOVTense({ povTenseId, povTense: { ...povTense, pov: { ...povTense.pov, characters } }, userId: project.data?.userId });
          set({ _povTenseSaving: false });
          if (!povTenseResponse) {
            return null;
          }
          return povTenseResponse;
        } catch (error) {
          Sentry.captureException(error, { tags: { feature: "setPOVTenseForDocument" } });
          set((state) => ({
            _povTenseSaving: false,
            ...(state._setDocumentEpoch === documentEpoch ? { currentDocumentPOVTense } : {}),
          }));
          if (previousDocumentPovTenseId !== povTenseId) {
            set((state) => ({
              document: state.document && state.document.id === doc.id ? { ...state.document, povTenseId: previousDocumentPovTenseId } : state.document,
            }));
          }
          return null;
        }
      },
      setDocumentPOVTense: async ({ documentId, povTenseId, disconnect }) => {
        if (!get().hasAccessToPOVAndTenseBeta) {
          return;
        }

        const project = get().project;
        if (!project) {
          return null;
        }

        if (!documentId) {
          return null;
        }

        if (disconnect) {
          if (get().currentDocumentPOVTenseStatus !== "ready") return null;
          // Create a new POV Tense for the document
          const newPOVTense = await POVTense.create(
            get().currentDocumentPOVTense.pov,
            get().currentDocumentPOVTense.tense,
            project.id,
            project.data.userId,
            documentId,
          );
          if (!newPOVTense) {
            return null;
          }

          await get().setDocument({
            ...get().document,
            povTenseId: newPOVTense.id,
          });
          await project.setDocumentPOVTense({ povTenseId: newPOVTense.id, documentId, userId: project.data?.userId });
          return;
        }

        // Optimistically update the POV Tense in the store
        const currentDocument = get().document;
        await get().setDocument({
          ...currentDocument,
          povTenseId,
        });

        // We have a POV Tense ID, so we can update the document directly
        if (povTenseId) {
          try {
            // Update the POV Tense in the Postgres DB
            await project.setDocumentPOVTense({ povTenseId, documentId, userId: project.data?.userId });
          } catch (error) {
            Sentry.captureException(error, { tags: { feature: "setDocumentPOVTense" } });
            // Rollback the optimistic update if the request fails
            set((state) => ({
              document: {
                ...state.document,
                povTenseId: currentDocument?.povTenseId || null,
              },
            }));
          }
          return;
        }
      },
      setMultiplePOV: async (value) => {
        if (!get().hasAccessToPOVAndTenseBeta) {
          return;
        }

        const previousState = normalizeIsMultiplePOVState(get().isMultiplePOV);
        const nextState = normalizeIsMultiplePOVState(value, previousState);

        if (_.isEqual(previousState, nextState)) {
          set({ isMultiplePOV: previousState });
          return;
        }

        // Optimistically update the isMultiplePOV in the store
        set({ isMultiplePOV: nextState });

        try {
          const project = get().project;
          if (!project) {
            return;
          }

          // Update the isMultiplePOV in the Postgres DB
          await project.setIsMultiplePOV(nextState, project.data?.userId, previousState);
        } catch (error) {
          Sentry.captureException(error, { tags: { feature: "setIsMultiplePOVForProject" } });
          set({ isMultiplePOV: previousState }); // Rollback if the request fails
        }
      },
      setCurrentDocumentPOVTense: (povTense) => {
        set({ currentDocumentPOVTense: povTense });
      },

      // Title cleaning animation functions
      startTitleAnimation: (projectId, oldTitle, newTitle) => {
        set((state) => ({
          titleAnimations: {
            ...state.titleAnimations,
            [projectId]: { oldTitle, newTitle, isAnimating: true },
          },
        }));
      },

      completeTitleAnimation: (projectId) => {
        set((state) => {
          const newAnimations = { ...state.titleAnimations };
          delete newAnimations[projectId];
          return { titleAnimations: newAnimations };
        });
      },

      getTitleAnimation: (projectId) => {
        return (state) => state.titleAnimations[projectId];
      },

      setManuscriptEditorGetter: (getter) => set({ manuscriptEditorGetter: getter ?? null }),

      getManuscriptEditor: () => {
        const g = get().manuscriptEditorGetter;
        if (typeof g === "function") {
          try {
            return g() ?? null;
          } catch {
            return null;
          }
        }
        return null;
      },
    }),
    {
      name: "column-widths",
      version: 1,
      // v1: collapsed rail widened from 25px to COLLAPSED_BAR_WIDTH to fit the
      // circular re-open buttons; fix up persisted widths from older sessions.
      migrate: (persistedState) => {
        const columnWidths = persistedState?.columnWidths;
        if (columnWidths) {
          for (const side of ["left", "right"]) {
            const column = columnWidths[side];
            if (!column) continue;
            column.collapsedMin = COLLAPSED_BAR_WIDTH;
            if (column.collapsed) {
              column.min = COLLAPSED_BAR_WIDTH;
              column.current = COLLAPSED_BAR_WIDTH;
            }
          }
        }
        return persistedState;
      },
      storage: createJSONStorage(() => createSafeStorage(typeof window !== "undefined" ? window.localStorage : null)),
      partialize: (state) => ({
        columnWidths: { left: state.columnWidths.left, right: state.columnWidths.right, center: state.columnWidths.center },
        // SW-8335 — persist Dean's per-document "user closed it" flag so the
        // comment sidebar does not auto-reopen on reload.
        userClosedCommentSidebarByDocId: state.userClosedCommentSidebarByDocId || {},
        commentSidebarWidth: clampCommentSidebarWidth(state.commentSidebarWidth ?? COMMENT_SIDEBAR_MIN_WIDTH),
      }),
    }
  )
);

export const withProjectStore = (BaseComponent) => {
  const MemoizedBaseComponent = React.memo(BaseComponent, (prev, next) => {
    return _.isEqual(prev, next);
  });

  return (props) => {
    const selector = (state) => {
      return {
        exitTrashView: state.exitTrashView,
        isEditingTextarea: state.isEditingTextarea,
        columnWidths: state.columnWidths,
        setColumnWidths: state.setColumnWidths,
        setUserScrolled: state.setUserScrolled,
        feedbackOpen: state.feedbackOpen,
        setFeedbackOpen: state.setFeedbackOpen,
        feedbackInvocation: state.feedbackInvocation,
        showNewDocumentCTAs: state.showNewDocumentCTAs,
        setShowNewDocumentCTAs: state.setShowNewDocumentCTAs,
        hasAutomaticTitle: state.hasAutomaticTitle,
        showLinkingMenu: state.showLinkingMenu,
        setShowLinkingMenu: state.setShowLinkingMenu,
        isCreatingNewBlankDocument: state.isCreatingNewBlankDocument,
        setIsCreatingNewBlankDocument: state.setIsCreatingNewBlankDocument,
        povTense: state.povTense,
        getPOVTense: state.getPOVTense,
        setPOVTense: state.setPOVTense,
      };
    };

    const selectedState = useProjectStore(selector);

    return (
      <MemoizedBaseComponent
        {...props}
        projectStore={selectedState}
      />
    );
  };
};
